import { ref, type Ref, computed, type ComputedRef } from 'vue'
import { defineStore } from 'pinia'
import { type IModuleContext, BaseModuleContext, ModuleListEnum } from '@/common/types/module'
import { type NavActionList, type NavItem, type NavActionResponse } from '@/common/types/navigation'
import { useSessionStore } from '@/common/stores/sessionStore'
import { buildNavigationItems, buildNavigationItem } from '@/common/services/NavActionsService'
import HttpService from '@/common/services/HttpService'

export const useNavigationStore = defineStore('navigation', () => {
  const response: Ref<NavActionResponse> = ref({})
  const context: Ref<IModuleContext> = ref(new BaseModuleContext())
  const ready: Ref<boolean> = ref(false)
  const disabled: Ref<boolean> = ref(false)

  async function loadNavigationActions() {
    try {
      const actionUrl = context.value.getActionEndpoint()
      if (actionUrl) {
        ready.value = false
        const actions = await HttpService.getData<NavActionResponse>(actionUrl)
        response.value = { ...actions }
      } else {
        response.value = {}
      }
    } catch (error) {
      console.log(error)
    } finally {
      ready.value = true
    }
  }

  const getHeaderItems: ComputedRef<NavItem[]> = computed(() => {
    if (response.value.header) {
      const navActions = response.value.header.filter((a) => a.type !== 'show-calendar')
      return [...buildNavigationItems(context.value, navActions)]
    } else {
      return []
    }
  })

  const getGlobalItems: ComputedRef<NavItem[]> = computed(() => {
    const items: NavItem[] = []

    // Create Calendar navigation items
    if (context.value.module === ModuleListEnum.CaseList && response.value.header) {
      const calendarAction = response.value.header.find((a) => a.type === 'show-calendar')
      if (calendarAction) {
        const calendarItems = buildNavigationItem(context.value, 'show-calendar', calendarAction)
        if (calendarItems.length) items.push(calendarItems[0])
      }
    }
    return items
  })

  const getToolbarItems: ComputedRef<NavItem[]> = computed(() => {
    const sessionStore = useSessionStore()
    const navActions: NavActionList = response.value.toolbar ? [...(response.value.toolbar as NavActionList)] : []

    return [...buildNavigationItems(context.value, navActions, [...sessionStore.authorities])]
  })

  return {
    response,
    context,
    ready,
    disabled,
    loadNavigationActions,
    getHeaderItems,
    getGlobalItems,
    getToolbarItems,
  }
})
